import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import '../../styles/search.css';
import type { Suggestion } from '../../types/search/Suggestion';

interface BaseSearchProps<T extends Suggestion> {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  suggestions: T[];
  onSelect: (item: T) => void; // クリック/エンター押下で候補を選択
  onEnter: (currentValue: string) => void; // エンター押下で候補を探す
  placeholder: string;
}

const BaseSearch = <T extends Suggestion>({
  value,
  onChange,
  suggestions = [],
  onSelect,
  onEnter,
  placeholder = '検索',
}: BaseSearchProps<T>) => {
  const [open, setOpen] = useState(false); // 検索候補の表示/非表示
  const [activeIndex, setActiveIndex] = useState(-1); // 検索候補のハイライト
  const containerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLUListElement>(null);

  // input外クリックで候補を閉じる（ポータル先も内側扱いにする）
  useEffect(() => {
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideInput = containerRef.current?.contains(target) ?? false;
      const insidePopup = popupRef.current?.contains(target) ?? false;
      if (!insideInput && !insidePopup) {
        setOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  // キーボードでの制御
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && suggestions.length > 0) {
        // 検索候補の選択
        onSelect(suggestions[activeIndex]);
        setOpen(false);
        setActiveIndex(-1);
      } else {
        // 検索
        setOpen(true);
        onEnter(value);
      }
      return;
    }

    if (suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((prev) =>
        prev === -1
          ? suggestions.length - 1
          : (prev - 1 + suggestions.length) % suggestions.length
      );
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  // リストをポータルで描画
  const listPortal =
    open && suggestions.length > 0 && containerRef.current
      ? createPortal(
          <ul
            ref={popupRef}
            className="base-search__list list-group shadow-sm"
            role="listbox"
            style={{
              position: 'absolute',
              top:
                containerRef.current.getBoundingClientRect().bottom +
                window.scrollY,
              left:
                containerRef.current.getBoundingClientRect().left +
                window.scrollX,
              width: containerRef.current.getBoundingClientRect().width,
              zIndex: 1000,
            }}
            onMouseDown={(e) => {
              // ドキュメントの mousedown にバブリングさせない（誤閉じ防止）
              e.stopPropagation();
            }}
          >
            {suggestions.map((item, index) => {
              const isActive = index === activeIndex;
              return (
                <li key={item.value} className="list-group-item p-0">
                  <button
                    id={`option-${item.value}`}
                    type="button"
                    role="option"
                    aria-selected={isActive}
                    className={`list-group-item list-group-item-action w-100 text-start py-2 ${
                      isActive ? 'active' : ''
                    }`}
                    onMouseDown={(e) => {
                      e.preventDefault(); // blur防止
                      e.stopPropagation(); // 誤閉じ防止
                    }}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => {
                      onSelect(item);
                      setOpen(false);
                      setActiveIndex(-1);
                    }}
                  >
                    {item.label}
                  </button>
                </li>
              );
            })}
          </ul>,
          document.body
        )
      : null;

  return (
    <div className="base-search" ref={containerRef}>
      <Form.Control
        type="text"
        value={value}
        onChange={(e) => onChange(e as React.ChangeEvent<HTMLInputElement>)}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoComplete="off"
      />
      {listPortal}
    </div>
  );
};

export default BaseSearch;
